import { useNavigate } from 'react-router-dom';
import { InfinityMark } from '@integra/ui';
import { BrandLogo, PUBLISHING_LOGOS } from './brandLogos.js';
import { enterpriseMailto } from '../lib/salesContact.js';

/** Named tools that carry a real brand mark on the integration cards. Tools
 *  without one (or whose owners ask not to appear in icon sets) show as text. */
const INTEGRATION_LOGO: Record<string, string> = {
  'Google Analytics 4': 'googleanalytics',
  'Google Ads': 'googleads',
  'Meta Ads': 'meta',
  'LinkedIn Ads': 'linkedin',
  HubSpot: 'hubspot',
  Mailchimp: 'mailchimp',
};

/**
 * Public landing surface (§3.3, §20.1) — the front door before the sign-in gate.
 *
 * Serious refresh, one-platform framing: INTEGRA is a single platform that does
 * three jobs completely — Activate, Govern, Optimise — every action running
 * through the same approval and audit spine. The visual language is the estate's
 * serious register (deep navy authority, a single garnet accent, Libre Franklin),
 * resolved entirely through the shared tokens — no colours invented here (§21.4).
 *
 * Deliberately client-agnostic (§4.1): the copy names no vertical and no tenant.
 */

/** Mono eyebrow — the label voice, on light or dark grounds. */
function Eyebrow({
  children,
  ground = 'light',
}: {
  children: React.ReactNode;
  ground?: 'light' | 'dark';
}) {
  return (
    <p
      className={`font-mono text-[11px] font-medium uppercase tracking-[0.18em] ${
        ground === 'dark' ? 'text-gold-400' : 'text-gold-text'
      }`}
    >
      {children}
    </p>
  );
}

/** A small right-pointing arrow bullet, garnet. */
function Arrow() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className="mt-0.5 shrink-0 text-gold-500"
    >
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Check() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className="mt-1 shrink-0 text-gold-500"
    >
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** One of the three capabilities the single platform delivers. */
function Pillar({
  idx,
  name,
  kicker,
  tag,
  points,
}: {
  idx: string;
  name: string;
  kicker: string;
  tag: string;
  points: string[];
}) {
  return (
    <div className="border-r border-edge p-8 last:border-r-0">
      <span className="font-mono text-xs tracking-[0.1em] text-ink-muted">{idx}</span>
      <h3 className="mt-5 flex items-baseline gap-2.5 font-heading text-2xl text-navy-700">
        {name}
        <span className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gold-text">
          {kicker}
        </span>
      </h3>
      <p className="mt-3.5 text-base font-semibold leading-snug text-navy-700">{tag}</p>
      <ul className="mt-5 flex flex-col gap-2.5">
        {points.map((p) => (
          <li key={p} className="flex gap-2.5 text-sm leading-snug text-ink-muted">
            <Arrow />
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One governance assurance card. */
function GCard({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="flex items-start gap-4 rounded-md border border-edge bg-surface p-5">
      <span className="mt-0.5 font-mono text-xs text-gold-500">{n}</span>
      <div>
        <h4 className="font-heading text-base text-navy-700">{title}</h4>
        <p className="mt-1 text-sm leading-relaxed text-ink-muted">{body}</p>
      </div>
    </div>
  );
}

/** A borderless hero metric — number leads (garnet), label supports. */
function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-balance font-heading text-2xl leading-tight text-gold-400 sm:text-4xl sm:leading-none">
        {value}
      </p>
      <p className="mt-2 text-xs uppercase tracking-wide text-navy-300">{label}</p>
    </div>
  );
}

/** One pricing tier. */
function Plan({
  name,
  tier,
  points,
  featured = false,
  cta,
  onCta,
}: {
  name: string;
  tier: string;
  points: string[];
  featured?: boolean;
  cta: string;
  onCta: () => void;
}) {
  return (
    <div
      className={`relative flex flex-col rounded-md border bg-surface p-7 ${
        featured ? 'border-gold-500 shadow-raised' : 'border-edge'
      }`}
    >
      {featured && (
        <span className="absolute -top-2.5 left-7 rounded-sm bg-gold-500 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-text-inverse">
          Recommended
        </span>
      )}
      <p className="font-heading text-2xl text-navy-700">{name}</p>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-muted">
        {tier}
      </p>
      <ul className="mb-6 mt-5 flex flex-col gap-2.5">
        {points.map((p) => (
          <li key={p} className="flex gap-2.5 text-sm text-ink-muted">
            <Check />
            {p}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onCta}
        className={`mt-auto w-full rounded-sm py-2.5 text-sm font-semibold transition-colors duration-fast ${
          featured
            ? 'bg-gold-500 text-text-inverse hover:bg-gold-600'
            : 'border border-border-strong text-navy-700 hover:border-navy-700'
        }`}
      >
        {cta}
      </button>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const signIn = () => navigate('/login');
  const goHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* ── Utility strip ───────────────────────────────────────── */}
      <div className="bg-navy-800 text-navy-300">
        <div className="mx-auto flex h-9 max-w-6xl items-center justify-between px-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em]">
            A Fruitful Bough platform
          </span>
          <a
            href="#platform"
            className="hidden font-mono text-[11px] uppercase tracking-[0.14em] text-ink-inverse hover:text-gold-400 sm:block"
          >
            Governed distribution for the enterprise
          </a>
        </div>
      </div>

      {/* ── Header ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 border-b border-edge bg-surface/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-7 px-6">
          <button
            type="button"
            onClick={goHome}
            aria-label="INTEGRA home"
            className="flex items-center gap-2.5 rounded-md transition-opacity duration-fast hover:opacity-80"
          >
            <InfinityMark size={26} className="text-gold-500" />
            <span className="font-heading text-lg text-navy-700">INTEGRA</span>
          </button>
          <nav className="ml-2 hidden items-center gap-6 md:flex">
            <a href="#platform" className="text-sm font-medium text-ink-muted hover:text-navy-700">
              Platform
            </a>
            <a href="#govern" className="text-sm font-medium text-ink-muted hover:text-navy-700">
              Governance
            </a>
            <a
              href="#integrations"
              className="text-sm font-medium text-ink-muted hover:text-navy-700"
            >
              Integrations
            </a>
          </nav>
          <span className="flex-1" />
          <button
            type="button"
            onClick={signIn}
            className="hidden text-sm font-semibold text-ink-muted hover:text-navy-700 sm:block"
          >
            Sign in
          </button>
          <a
            href="#plans"
            className="rounded-sm bg-gold-500 px-4 py-2 text-sm font-semibold text-text-inverse transition-colors duration-fast hover:bg-gold-600"
          >
            See the platform →
          </a>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-navy-800 text-ink-inverse">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-gold-glow"
        />
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-14 lg:grid-cols-[1.15fr_0.85fr] lg:py-16">
          <div>
            <Eyebrow ground="dark">Enterprise multi-channel activation</Eyebrow>
            <h1 className="mt-4 font-heading text-4xl leading-[1.06] sm:text-5xl lg:text-[3.4rem]">
              Publish once. Govern everything. Land native on every channel.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-navy-300">
              INTEGRA is the governed distribution layer for enterprise marketing teams and their
              agencies — one asset adapted and shipped across every organic and paid channel, with
              approval, brand safety, and an append-only audit on every move.
            </p>
            <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-navy-200">
              {[
                'No CRM lock-in',
                'Priced on activation, not seats',
                'Live in days, not quarters',
              ].map((wedge) => (
                <li key={wedge} className="flex items-center gap-1.5">
                  <span aria-hidden className="text-gold-400">
                    ✓
                  </span>
                  {wedge}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#plans"
                className="rounded-sm bg-gold-500 px-5 py-2.5 text-sm font-semibold text-text-inverse transition-colors duration-fast hover:bg-gold-600"
              >
                See the platform →
              </a>
              <button
                type="button"
                onClick={() => navigate('/demo')}
                className="rounded-sm border border-navy-500 px-4 py-2.5 text-sm text-ink-inverse transition-colors duration-fast hover:border-gold-400 hover:text-gold-400"
              >
                Watch the 90-second demo
              </button>
            </div>
          </div>

          {/* Right: the live "governed action" console — governance made visible. */}
          <div className="rounded-xl border border-navy-500 bg-navy-900 p-5 shadow-raised">
            <div className="flex items-center justify-between border-b border-navy-600 pb-3">
              <span className="font-mono text-xs uppercase tracking-wide text-gold-400">
                Next governed action
              </span>
              <span className="rounded-full border border-navy-500 px-2 py-0.5 text-[10px] text-navy-300">
                Awaiting approval
              </span>
            </div>
            <p className="mt-4 font-heading text-lg text-ink-inverse">
              Publish the autumn launch across 7 channels.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-navy-300">
              Rendered and staged. One approval releases it; a failure rolls the whole run back.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[
                ['Render', 'Done'],
                ['Approve', 'Now'],
                ['Publish', 'Queued'],
              ].map(([step, state]) => (
                <div
                  key={step}
                  className="rounded-md border border-navy-600 bg-navy-800 px-3 py-2.5"
                >
                  <p className="text-xs text-ink-inverse">{step}</p>
                  <p
                    className={`mt-1 text-[10px] uppercase tracking-wide ${
                      state === 'Done'
                        ? 'text-gold-400'
                        : state === 'Now'
                          ? 'text-ink-inverse'
                          : 'text-navy-300'
                    }`}
                  >
                    {state}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* metrics band */}
        <div className="border-t border-navy-600 bg-navy-900">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-6 py-6 sm:grid-cols-4">
            <Metric value="1 action" label="Publish to every connected channel" />
            <Metric value="16+" label="Channels & sources, one hub" />
            <Metric value="100%" label="Of changes on the audit trail" />
            <Metric value="0 seats" label="Priced on activation" />
          </div>
        </div>
      </section>

      {/* ── One platform, three jobs ────────────────────────────── */}
      <section id="platform" className="mx-auto max-w-6xl px-6 py-16">
        <Eyebrow>One platform</Eyebrow>
        <h2 className="mt-3 max-w-3xl font-heading text-3xl text-navy-700 sm:text-4xl">
          One platform. Everything your team ships, governed.
        </h2>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-muted">
          INTEGRA does three jobs completely — activate, govern, optimise — and every action runs
          through the same approval and audit spine, so scale never outruns control.
        </p>
        <div className="mt-9 grid overflow-hidden rounded-md border border-edge md:grid-cols-3">
          <Pillar
            idx="01"
            name="Activate"
            kicker="Distribute"
            tag="One asset, every channel — native on each, organic and paid."
            points={[
              'One-to-all fan-out with per-channel adaptation',
              'Scheduling & sequencing across time zones',
              'Creative-source import from Canva & AI design',
              'Faithful per-platform preview before it ships',
            ]}
          />
          <Pillar
            idx="02"
            name="Govern"
            kicker="Assure"
            tag="Nothing ships without sign-off — and everything leaves a trail."
            points={[
              'Agency-client approval gates on every campaign',
              'Brand-safety rules enforced before publish',
              'Paid budget & bid changes under human approval',
              'Append-only audit — who changed what, when',
            ]}
          />
          <Pillar
            idx="03"
            name="Optimise"
            kicker="Intelligence"
            tag="Measure organic and paid as one — then let the evidence decide."
            points={[
              'Unified organic + paid measurement, incl. GA4',
              'Measured and estimated shown apart, never blended',
              'Slot-level A/B experiments with a clear winner',
              'A weekly review that recommends cut or double-down',
            ]}
          />
        </div>
      </section>

      {/* ── Governance band ─────────────────────────────────────── */}
      <section id="govern" className="border-y border-edge bg-surface-muted">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid items-center gap-16 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <Eyebrow>The difference</Eyebrow>
              <p className="mt-3 font-heading text-3xl leading-tight text-navy-700 sm:text-[2.1rem]">
                Most platforms help you move faster. INTEGRA lets you move fast{' '}
                <span className="text-gold-text">and stay in control.</span>
              </p>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-muted">
                Governance isn&rsquo;t a settings page bolted on the side — it&rsquo;s the spine
                every action runs through. Approval, brand safety, and audit are enforced by the
                system, not by a policy document nobody reads.
              </p>
            </div>
            <div className="flex flex-col gap-3.5">
              <GCard
                n="01"
                title="Approve before it ships"
                body="Campaigns pause at a human gate. No asset reaches a channel until the right person signs off."
              />
              <GCard
                n="02"
                title="Safe by rule, not by hope"
                body="Prohibited and review terms are checked at publish time, per workspace — enforced, not advisory."
              />
              <GCard
                n="03"
                title="Every change on the record"
                body="An append-only ledger captures who changed what and when — including the before-and-after on paid spend."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Integrations ────────────────────────────────────────── */}
      <section id="integrations" className="mx-auto max-w-6xl px-6 py-16">
        <Eyebrow>The integration hub</Eyebrow>
        <h2 className="mt-3 max-w-3xl font-heading text-3xl text-navy-700 sm:text-4xl">
          Connects to the stack you already run.
        </h2>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-muted">
          Analytics, CRM, email, ads, design and collaboration — connected once, with a click, and
          governed the same way as everything else. No CRM lock-in.
        </p>
        <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['Analytics', 'Source', ['Google Analytics 4', 'Website & audience']],
            ['Advertising', 'Paid', ['Google Ads', 'Meta Ads', 'LinkedIn Ads']],
            ['CRM', 'Source', ['HubSpot', 'Salesforce']],
            ['Email & automation', 'Channel', ['Mailchimp', 'Klaviyo']],
            ['Design', 'Creative', ['Canva', 'AI image generation']],
            ['Collaboration', 'Notify', ['Slack', 'Microsoft Teams']],
          ].map(([cat, tag, items]) => (
            <div key={cat as string} className="rounded-md border border-edge bg-surface p-5">
              <div className="flex items-center justify-between">
                <h4 className="font-heading text-sm text-navy-700">{cat as string}</h4>
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  {tag as string}
                </span>
              </div>
              <div className="mt-3.5 flex flex-wrap gap-1.5">
                {(items as string[]).map((it) => (
                  <span
                    key={it}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-border-strong bg-surface-muted px-2 py-1 text-xs text-ink-muted"
                  >
                    {INTEGRATION_LOGO[it] !== undefined && (
                      <BrandLogo id={INTEGRATION_LOGO[it]} size={13} className="text-navy-700" />
                    )}
                    {it}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Publishing logo wall — real brand marks, monochrome for cohesion. */}
        <div className="mt-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-muted">
            Social &amp; publishing
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-7 gap-y-4">
            {PUBLISHING_LOGOS.map((id) => (
              <BrandLogo
                key={id}
                id={id}
                size={26}
                className="text-navy-700 opacity-80 transition-opacity duration-fast hover:opacity-100"
              />
            ))}
            <span className="inline-flex items-center rounded-sm border border-border-strong px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-muted">
              + more
            </span>
          </div>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-ink-muted">
            A selection of what connects today — not an exhaustive list. Any tool in your stack can
            be added through the integration hub, and new connectors ship continuously.
          </p>
        </div>
      </section>

      {/* ── Plans / sign-up ─────────────────────────────────────── */}
      <section id="plans" className="border-y border-edge bg-surface-muted">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <Eyebrow>See the platform</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl text-navy-700 sm:text-4xl">
            Start on your terms.
          </h2>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-muted">
            Create your workspace in minutes on Bronze, then step up to Silver or Gold as your
            channels, volume and team grow.
          </p>
          <div className="mt-9 grid gap-4 lg:grid-cols-3">
            <Plan
              name="Bronze"
              tier="Starter"
              points={[
                'The full governed publish loop',
                'Approval gates, brand safety & audit',
                'One workspace to get moving',
              ]}
              cta="Get started"
              onCta={signIn}
            />
            <Plan
              name="Silver"
              tier="Scale up"
              featured
              points={[
                'More channels, assets & volume',
                'Paid-media management under approval',
                'GA4 & the full integration hub',
              ]}
              cta="Get started"
              onCta={signIn}
            />
            <Plan
              name="Gold"
              tier="Enterprise"
              points={[
                'Highest limits across the board',
                'Multi-brand white-label workspaces',
                'Priority support & onboarding',
              ]}
              cta="Talk to us"
              onCta={() => {
                window.location.href = enterpriseMailto();
              }}
            />
          </div>
          <p className="mt-6 font-mono text-[13px] tracking-[0.02em] text-ink-muted">
            {'// '}Every tier is governed the same way — approval, brand safety and audit are never
            an upsell.
          </p>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-navy-800">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-[360px] w-[360px] rounded-full bg-gold-glow"
        />
        <div className="relative mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-8 px-6 py-14">
          <div>
            <h2 className="max-w-md font-heading text-3xl text-ink-inverse sm:text-4xl">
              See your own campaign, governed end to end.
            </h2>
            <p className="mt-3 max-w-md text-navy-300">
              Sign in to your workspace, or take the administrator overview of the estate.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="#plans"
              className="rounded-sm bg-gold-500 px-5 py-2.5 text-sm font-semibold text-text-inverse transition-colors duration-fast hover:bg-gold-600"
            >
              See the platform →
            </a>
            <button
              type="button"
              onClick={signIn}
              className="rounded-sm border border-navy-500 px-4 py-2.5 text-sm text-ink-inverse transition-colors duration-fast hover:border-gold-400 hover:text-gold-400"
            >
              Sign in
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="border-t border-edge bg-surface">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 sm:flex-row">
          <button
            type="button"
            onClick={goHome}
            aria-label="INTEGRA home"
            className="flex items-center gap-2.5 rounded-md transition-opacity duration-fast hover:opacity-80"
          >
            <InfinityMark size={22} className="text-gold-500" />
            <span className="font-heading text-base text-navy-700">INTEGRA</span>
          </button>
          <p className="text-xs text-ink-muted">
            © 2026 Fruitful Bough · INTEGRA · Enterprise Multi-Channel Distribution &amp; Activation
          </p>
        </div>
      </footer>
    </div>
  );
}
